import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type SwitchSize = 'sm' | 'md';

export interface SwitchProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'onChange' | 'onClick'
  > {
  checked: boolean;
  label: ReactNode;
  onCheckedChange: (checked: boolean) => void;
  size?: SwitchSize;
}

const labelSizeClassNames: Record<SwitchSize, string> = {
  sm: 'tw-text-sm tw-leading-[18px]',
  md: 'tw-text-sm tw-leading-[18px] md:tw-text-base md:tw-leading-[22px]',
};

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      label,
      onCheckedChange,
      size = 'md',
      className = '',
      disabled,
      type = 'button',
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={[
        'tw-inline-flex tw-items-center tw-gap-2 tw-text-text-primary focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary focus-visible:tw-ring-offset-2 disabled:tw-opacity-disabled',
        labelSizeClassNames[size],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => onCheckedChange(!checked)}
      {...props}
    >
      <span
        className={[
          'tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-shrink-0 tw-rounded-full tw-transition-colors',
          checked ? 'tw-bg-primary' : 'tw-bg-surface-disabled',
        ].join(' ')}
        aria-hidden="true"
      >
        <span
          className={[
            'tw-absolute tw-left-0.5 tw-top-0.5 tw-size-5 tw-rounded-full tw-bg-surface tw-shadow-sm tw-transition-transform',
            checked ? 'tw-translate-x-5' : 'tw-translate-x-0',
          ].join(' ')}
        />
      </span>
      <span>{label}</span>
    </button>
  ),
);

Switch.displayName = 'Switch';
